"use client";

import React from "react";
import { capitalize, Paper, Typography } from "@mui/material";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { formatPercent } from "@/utils/valueFormatters";
import { ChartDataProvider } from "@mui/x-charts/ChartDataProvider";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { chartColors, chartColorsDark } from "@/theme/chartColors";
import { useTranslation } from "react-i18next";
import { useDealership } from "@/contexts/DealershipContext";

type MonthlyData = {
    [key: string]: number;
};

type RollingData = {
    [key: string]: number | null;
};

type TurnoverMonthlyChartProps = {
    data: {
        peer_group_monthly: MonthlyData;
        peer_group_rolling: RollingData;
        your_dealership_monthly: MonthlyData;
        your_dealership_rolling: RollingData;
        all_dealerships_monthly: MonthlyData;
        all_dealerships_rolling: RollingData;
    };
    title?: string;
    isGroup?: boolean;
};

const TurnoverMonthlyChart: React.FC<TurnoverMonthlyChartProps> = ({
    data,
    title,
    isGroup = false,
}) => {
    const { t } = useTranslation();
    const { metadata } = useDealership();

    // Default the chart title to the backend-resolved data year ("{year} Turnover")
    // so it advances with the study instead of being hardcoded (ES-956).
    const resolvedTitle =
        title ?? t("executiveSummary.sections.turnoverYear", { year: metadata?.data_year });

    const labels = Object.keys(data.your_dealership_monthly).map((month, idx) =>
        capitalize(t("time.monthDisplay", { value: new Date(2000, idx), format: "shortMonth" }))
    );

    const monthlyData = [
        ...Object.values(data.your_dealership_monthly),
        ...Object.values(data.peer_group_monthly),
        ...Object.values(data.all_dealerships_monthly),
    ];

    const rollingData = [
        ...Object.values(data.your_dealership_rolling),
        ...Object.values(data.peer_group_rolling),
        ...Object.values(data.all_dealerships_rolling),
    ].filter((v) => v !== null) as number[];

    const series = [
        {
            type: "bar" as const,
            label: isGroup
                ? t("common.labels.yourGroupMonthly")
                : t("common.labels.yourDealershipMonthly"),
            data: Object.values(data.your_dealership_monthly),
            color: chartColors[0],
            yAxisId: "monthly",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },

        {
            type: "bar" as const,
            label: t("common.labels.peerGroupMonthly"),
            data: Object.values(data.peer_group_monthly),
            color: chartColors[1],
            yAxisId: "monthly",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },
        {
            type: "bar" as const,
            label: t("common.labels.allDealershipMonthly"),
            data: Object.values(data.all_dealerships_monthly),
            color: chartColors[2],
            yAxisId: "monthly",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },
        {
            type: "line" as const,
            label: t("common.labels.yourRollingAvg"),
            data: Object.values(data.your_dealership_rolling),
            color: chartColorsDark[0],
            yAxisId: "rolling",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },
        {
            type: "line" as const,
            label: t("common.labels.peerRollingAvg"),
            data: Object.values(data.peer_group_rolling),
            color: chartColorsDark[1],
            yAxisId: "rolling",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },
        {
            type: "line" as const,
            label: t("common.labels.allRollingAvg"),
            data: Object.values(data.all_dealerships_rolling),
            color: chartColorsDark[2],
            yAxisId: "rolling",
            valueFormatter: (value: number) =>
                formatPercent(value === null ? null : value * 100, 1),
        },
    ];

    return (
        <Paper
            className="exec-step-7"
            elevation={0}
            sx={{ p: 2, borderRadius: "16px", border: "1px solid #dee2e6" }}
        >
            <Typography
                variant="h6"
                component="h3"
                sx={{ mb: 2, fontWeight: "bold", color: "#002046" }}
            >
                {resolvedTitle}
            </Typography>
            <ChartDataProvider
                series={series as React.ComponentProps<typeof ChartContainer>["series"]}
                height={400}
                xAxis={[
                    {
                        scaleType: "band",
                        data: labels,
                    },
                ]}
                yAxis={[
                    {
                        id: "monthly",
                        position: "left",
                        data: monthlyData,
                        valueFormatter: (value: number | null) =>
                            formatPercent((value ?? 0) * 100, 0),
                    },
                    {
                        id: "rolling",
                        position: "right",
                        data: rollingData,
                        valueFormatter: (value: number | null) =>
                            formatPercent((value ?? 0) * 100, 0),
                    },
                ]}
            >
                <ChartsLegend />
                <ChartsSurface>
                    <BarPlot />
                    <LinePlot />
                    <ChartsXAxis />
                    <ChartsYAxis axisId="monthly" />
                    <ChartsYAxis axisId="rolling" />
                    <ChartsTooltip />
                </ChartsSurface>
            </ChartDataProvider>
        </Paper>
    );
};

export default TurnoverMonthlyChart;
